import dayjs from "dayjs"
import { useCallback } from "react"
import { usePolyglot } from "src/contexts"
import { useToday } from "src/hooks/time"
import type { DateTimeFormatOptions } from "src/types"

// formats a date-time string like "Tue, Jun 9"
const dayOfTheWeek: DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
}

interface DateOptions {
  dateTimeFormatOptions?: DateTimeFormatOptions;
  lowercase?: boolean;
}

/*
 * render just the date from a date-time string as either:
 * - "Tomorrow"
 * - "Today"
 * - "Yesterday"
 * - a string formatted for the appropriate locale
 */
export function useDisplayDate(options: DateOptions = {}) {
  const {
    lowercase = false,
    dateTimeFormatOptions = dayOfTheWeek,
  } = options

  const polyglot = usePolyglot()
  const today = useToday()

  return useCallback(
    (day: string) => {
      if (day === "") {
        day = dayjs().toISOString() // Default to today if no date is provided
      }

      const startOfToday = dayjs(today).startOf("day")
      const startOfDay = dayjs(day).startOf("day")
      const diff = startOfToday.diff(
        startOfDay,
        "days",
      )

      if (polyglot) {
        // say "Tomorrow" if the difference in days is -1
        if (diff === -1) {
          return polyglot.t(`time.${lowercase ? "tomorrow" : "tomorrow_cap"}`)
        }

        // say "Today" if the difference in days is 0
        if (diff === 0) {
          return polyglot.t(`time.${lowercase ? "today" : "today_cap"}`)
        }

        // say "Yesterday" if the difference in days is 1
        if (diff === 1) {
          return polyglot.t(`time.${lowercase ? "yesterday" : "yesterday_cap"}`)
        }
      }

      // otherwise format the date
      return new Intl.DateTimeFormat(
        polyglot.locale(),
        dateTimeFormatOptions,
      ).format(dayjs(day).toDate())
    },
    [
      polyglot,
      lowercase,
      dateTimeFormatOptions,
      today,
    ],
  )
}
